import { FormikProps } from 'formik';
import { PropsWithChildren, createContext, useContext, useMemo, useState } from 'react';
import { BridgeFiatRail, MerchantInfo } from 'services/merchants/types';

import { PWCAsset } from 'types/assets';
import { CurrencyCode } from 'types/currency';

import { PayoutDestinationsDataRow } from '../../../types';
import { ACHFormValues } from '../../createAccount/components/forms/ach.keys';
import { BrebFormValues } from '../../createAccount/components/forms/breb.keys';
import { PixFormValues } from '../../createAccount/components/forms/pix.keys';
import { SepaFormValues } from '../../createAccount/components/forms/sepa.keys';
import { SpeiFormValues } from '../../createAccount/components/forms/spei.keys';
import { SwiftFormValues } from '../../createAccount/components/forms/swift.keys';
import { PayoutType } from '../types';

interface PayoutState {
  merchant: MerchantInfo;
  isDirectPaymentFlow: boolean;
  editing: PayoutDestinationsDataRow | null;
  [PayoutType.Fiat]: {
    currencyCode: CurrencyCode | null;
    paymentRail?: BridgeFiatRail;
    bankAccountUuid?: string;
    memo?: string;
    [BridgeFiatRail.ACH]?: {
      form: FormikProps<ACHFormValues>;
    };
    [BridgeFiatRail.ACH_SAME_DATE]?: {
      form: FormikProps<ACHFormValues>;
    };
    [BridgeFiatRail.WIRE]?: {
      form: FormikProps<ACHFormValues>;
    };
    [BridgeFiatRail.SWIFT]?: {
      form: FormikProps<SwiftFormValues>;
    };
    [BridgeFiatRail.SEPA]?: {
      form: FormikProps<SepaFormValues>;
    };
    [BridgeFiatRail.SPEI]?: {
      form: FormikProps<SpeiFormValues>;
    };
    [BridgeFiatRail.PIX]?: {
      form: FormikProps<PixFormValues>;
    };
    [BridgeFiatRail.BRE_B]?: {
      form: FormikProps<BrebFormValues>;
    };
  };
  [PayoutType.Crypto]: {
    cryptocurrency?: PWCAsset;
  };
}

const initialPayoutState =
  (paymentRail?: BridgeFiatRail) =>
  (merchant: MerchantInfo, editing: PayoutDestinationsDataRow | null, isDirectPaymentFlow: boolean): PayoutState => ({
    merchant,
    editing,
    isDirectPaymentFlow,
    [PayoutType.Fiat]: {
      currencyCode: null,
      paymentRail,
    },
    [PayoutType.Crypto]: {
      cryptocurrency: undefined,
    },
  });

interface PayoutContextType extends PayoutState {
  setPayoutState: (newPayoutState: PayoutState | null) => void;
}

const PayoutContext = createContext<PayoutContextType | undefined>(undefined);

export const usePayout = () => {
  const context = useContext(PayoutContext);
  if (!context) {
    throw new Error('usePayoutContext must be used within a PayoutProvider');
  }
  return context;
};

export const PayoutProvider = ({
  merchant,
  editing,
  isDirectPaymentFlow,
  children,
}: PropsWithChildren & { merchant: MerchantInfo; editing?: PayoutDestinationsDataRow | null; isDirectPaymentFlow: boolean }) => {
  const [localPayout, setPayoutLocal] = useState<PayoutState>(
    initialPayoutState(merchant.paymentRail)(merchant, editing ?? null, isDirectPaymentFlow),
  );

  const setPayoutState = (newPayout: PayoutState | null) => {
    setPayoutLocal((prevPayout) => ({
      ...prevPayout,
      ...newPayout,
      setPayoutState,
    }));
  };

  const contextValue = useMemo(
    () => ({
      ...localPayout,
      setPayoutState,
    }),
    [localPayout],
  );

  return <PayoutContext.Provider value={contextValue}>{children}</PayoutContext.Provider>;
};
